/**
 * #690: the one filtering path the Operations → Memory page's graph and list views both read, so a search or
 * a team filter can never narrow one of them differently from the other (the CTO's triage decision: "graph and
 * list share one query path, list is the accessible baseline"), plus the plain statement a bounded page owes
 * its reader — that what it shows is a subset of what the enterprise holds.
 */

import {
    type MemoryConstellationResponse,
    type MemoryConstellationUnit,
    type MemoryGraphQuery,
    type MemoryRecallHealth,
    type MemoryUnitType,
    matchesMemoryQuery,
} from '@zerohuman/shared'
import type { ConstellationUnitView } from '@zerohuman/ui'
import type { MemoryGraphNode } from '@/memory-graph-api'

/** What `describeMemorySubset` tells the page: whether this is a subset, and the sentence to show when it is. */
export type MemorySubset = { truncated: boolean; label: string }

/**
 * The nodes matching every filter the caller named. An absent filter narrows nothing; a named one narrows
 * exactly, so a `fact` is never returned for a `knowledgeType: 'inference'` query and vice versa (#690 AC3).
 */
export function filterMemoryNodes(nodes: MemoryGraphNode[], query: MemoryGraphQuery = {}): MemoryGraphNode[] {
    return nodes.filter(node => matchesMemoryQuery(node, query))
}

/**
 * Whether a page of `shown` nodes out of `total` is a subset, and the notice that says so. A page holding
 * everything the enterprise has says nothing; a bounded one names both numbers rather than implying the graph
 * on screen is the whole of memory (#690 AC5).
 *
 * `moreThanCounted` is the server saying its own count is partial: a search or a knowledge-type filter is decided
 * over built nodes and stops at a scan ceiling, so there can be matches it never read. `total` is then a floor
 * rather than the whole, and a page carrying every node counted is still a subset — so it is named as one
 * instead of the reader being left to assume they are looking at all of memory.
 */
export function describeMemorySubset(total: number, shown: number, moreThanCounted = false): MemorySubset {
    if (shown < total) return { truncated: true, label: `Showing ${shown} of ${total}` }
    if (moreThanCounted) return { truncated: true, label: `Showing ${shown} of the most recently recorded` }
    return { truncated: false, label: '' }
}

/** What `describeRecallHealth` tells the page: the sentence to show, and whether it is past the target. */
export type RecallHealthLine = { label: string; overTarget: boolean }

/** A share as a percentage a person reads: one decimal below ten, none above. */
function percent(share: number): string {
    const value = share * 100
    return `${value < 10 ? value.toFixed(1) : Math.round(value)}%`
}

/**
 * How often memory could be read when a run or a chat reply asked it, as one sentence. Null when nothing asked
 * in the window: there is no rate to report, and an empty line would read as a problem.
 */
export function describeRecallHealth(health: MemoryRecallHealth): RecallHealthLine | null {
    const unavailable = health.runs.unavailable + health.chats.unavailable
    const attempted = unavailable + health.runs.consulted + health.chats.consulted
    if (attempted === 0 || health.unavailableShare === null) {
        return null
    }
    const window = `Recall, last ${health.windowHours} hours`
    if (unavailable === 0) {
        return { label: `${window}: memory answered all ${attempted} times it was asked.`, overTarget: false }
    }
    const overTarget = health.unavailableShare > health.target
    const failed = `${unavailable} of ${attempted} recalls could not be read (${percent(health.unavailableShare)})`
    return {
        label: `${window}: ${failed}${overTarget ? `, above the ${percent(health.target)} target.` : '.'}`,
        overTarget,
    }
}

/** A moment as this page writes every timestamp: UTC, to the minute, so the date does not move with the reader. */
export function memoryMoment(iso: string): string {
    return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`
}

/** What each kind of memory unit is called on the constellation. A unit whose kind the store does not say is a memory. */
const UNIT_WORDS: Record<MemoryUnitType, string> = {
    world: 'World',
    experience: 'Experience',
    observation: 'Observation',
}

/**
 * The store's units as the kit's constellation draws them. A unit opens when it names the note it came from; an
 * observation, consolidated from several notes, has none to open.
 */
export function constellationUnits(units: readonly MemoryConstellationUnit[]): ConstellationUnitView[] {
    return units.map(unit => {
        const at = unit.mentionedAt === null ? Number.NaN : Date.parse(unit.mentionedAt)
        const dated = unit.mentionedAt !== null && !Number.isNaN(at)
        return {
            id: unit.id,
            text: unit.text,
            typeWord: unit.type === null ? 'Memory' : UNIT_WORDS[unit.type],
            at: dated ? at : null,
            ...(dated ? { atWord: memoryMoment(new Date(at).toISOString()) } : {}),
            sourceFacts: unit.sourceFacts,
            entities: unit.entities,
            opens: unit.noteId !== null,
        }
    })
}

/** The units that came from one note: what the constellation rings while that note is being read. */
export function unitsOfNote(units: readonly MemoryConstellationUnit[], noteId: string | null): string[] {
    if (noteId === null) return []
    return units.filter(unit => unit.noteId === noteId).map(unit => unit.id)
}

/** What `describeConstellation` tells the page: whether the map could be read, and what to say under it. */
export type ConstellationNotice = { unavailable: boolean; lines: string[] }

/**
 * What the page owes its reader about the constellation it drew. An unread store is said as that, never drawn as
 * an empty memory: the list under the map comes from the notes index and is still complete. A map drawn from part
 * of memory names both numbers, and one drawn without a store says why it has no links.
 */
export function describeConstellation(constellation: MemoryConstellationResponse | null): ConstellationNotice {
    if (constellation === null || constellation.status === 'unavailable') {
        return {
            unavailable: true,
            lines: ['The memory map could not be read just now. Every entry is still listed below.'],
        }
    }
    const lines: string[] = []
    if (constellation.truncated) {
        lines.push(`The map is drawn from ${constellation.scanned} of ${constellation.total} memories.`)
    }
    if (constellation.source === 'index') {
        lines.push('No memory store is connected here, so memories are drawn without the links between them.')
    }
    return { unavailable: false, lines }
}
